import { Divider, Slider, Space, Text } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { t } from "i18next";

import { getHasFiltersParams } from "#modules/similarPart/view/filters/getHasFiltersParams";
import { SimilarPartFilters } from "#modules/similarPart/view/filters/SimilarPartFilters";
import { Icon } from "#shared/icons/Icon";
import { Modal } from "#shared/view/components/Modal";
import { Button } from "#shared/view/designSystem/Button";
import { FilterQuery } from "shared";

import { Switch } from "./Switch";
import { Title } from "./Title";

export const DEFAULT_DIMENSION_WEIGHT = 0.01;
const DIMENSION_WEIGHT_STEPS = [0, DEFAULT_DIMENSION_WEIGHT, 0.05, 0.15];

type Props = {
  filtersParams: FilterQuery | undefined;
  updateFilters: (params: FilterQuery) => void;
  refetch: () => void;
  hasDimensionsFilter: boolean;
  toggleDisplayFeatures?: () => void;
  displayFeatures?: boolean;
  toggleDisplayScore?: () => void;
  displayScore?: boolean;
  dimensionWeight?: number;
  setDimensionWeight?: (value: number) => void;
};

export const FilterButton = ({
  filtersParams,
  updateFilters,
  refetch,
  hasDimensionsFilter,
  toggleDisplayFeatures,
  displayFeatures,
  toggleDisplayScore,
  displayScore,
  dimensionWeight = DEFAULT_DIMENSION_WEIGHT,
  setDimensionWeight,
}: Props) => {
  const [isModalOpened, { open, close }] = useDisclosure(false);

  const hasFiltersParams = getHasFiltersParams(filtersParams);

  const onUpdateFilters = (data: FilterQuery) => {
    updateFilters(data);
    refetch();
    close();
  };

  return (
    <>
      <Button variant="outline" icon={<Icon name="slider" />} onClick={open}>
        {hasFiltersParams ? t("similarPart.filter.modify") : t("similarPart.filter.filter")}
      </Button>
      <Modal
        opened={isModalOpened}
        onClose={close}
        title={t("similarPart.filter.title")}
        size={"lg"}
      >
        <SimilarPartFilters
          onUpdateFilters={onUpdateFilters}
          filtersParams={filtersParams}
          hasDimensionsFilter={hasDimensionsFilter}
        />

        {toggleDisplayFeatures && (
          <>
            <Space my="sm" />
            <Divider />
            <Space my="sm" />
            <Title order={6}>{t("similarPart.filter.displayOptions")}</Title>
            <Space my="xs" />
            <Switch
              checked={displayFeatures}
              onChange={toggleDisplayFeatures}
              label={t("similarPart.filter.displayFeatures")}
            />
            <Space my="xxs" />
            <Switch
              checked={displayScore}
              onChange={toggleDisplayScore}
              label={t("similarPart.filter.displayScore")}
            />
          </>
        )}

        {setDimensionWeight && (
          <>
            <Space my="sm" />
            <Divider />
            <Space my="sm" />
            <Title order={6}>{t("similarPart.filter.rankingOptions")}</Title>
            <Space my="xs" />
            <Text size="sm">{t("similarPart.filter.dimensionWeight")}</Text>
            <Space my="xs" />
            <Slider
              value={DIMENSION_WEIGHT_STEPS.indexOf(dimensionWeight)}
              onChange={(v) => setDimensionWeight(DIMENSION_WEIGHT_STEPS[v] ?? 0)}
              onChangeEnd={() => refetch()}
              min={0}
              max={3}
              step={1}
              marks={[
                { value: 0, label: t("similarPart.filter.dimensionWeightNone") },
                { value: 1, label: t("similarPart.filter.dimensionWeightLow") },
                { value: 2, label: t("similarPart.filter.dimensionWeightMedium") },
                { value: 3, label: t("similarPart.filter.dimensionWeightStrong") },
              ]}
              mb="xl"
            />
          </>
        )}
      </Modal>
    </>
  );
};
